import React, { useContext, useEffect, useRef, useState } from 'react';
import classes from './PostEditorBulkImportGroupElement.module.scss';
import classNames from 'classnames';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import { PostEditorHeader } from '../PostEditorHeader';
import { PostEditorBody } from '../PostEditorBody';
import { PostEditorGroupDataProvider } from '../states/PostEditorGroupDataProvider';
import { UseFormReturn } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import { PostEditorContext } from '../states/PostEditorProvider';
import { useDimensions } from '../../../hooks/useDimensions';
import { Icon } from 'components/images/Icon/Icon';
import { getErrorsCounter } from '../helpers/getErrorsCounter';
import { POST_EDITOR_ERROR_CLASS } from '../../../constants/styleConstants';
import { Debugger } from '../../../components/containers/Debugger';

export type PostEditorBulkImportGroupElementViewProps = {
  //
  index: number;
  counter: number;
  formInstance: UseFormReturn;
  removeGroupAction: (index: number) => void;
};

export const PostEditorBulkImportGroupElementView = (props: PostEditorBulkImportGroupElementViewProps): JSX.Element => {
  const { index, formInstance, removeGroupAction, counter } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const [dynamicHeight, setDynamicHeight] = useState<number>(394);
  const [animated, setAnimated] = useState<boolean>(false);
  const { state, dispatch } = useContext(PostEditorContext);
  const { postGroups } = state;
  const postGroup = postGroups[index];
  const expanded = postGroup.expanded;
  const [totalErrors, totalWarrnings] = getErrorsCounter([postGroup]);

  const { ref, inView } = useInView();

  useEffect(() => {
    if (!expanded) {
      // we use timeout to set height once all the fade in animations are completed, and the height is stable.
      // all fade animations are set to 300ms and that's why we use 350 to make sure all is set
      setTimeout(() => setAnimated(true), 350);
    }
  }, [expanded]);

  const { height } = useDimensions(elementRef);

  useEffect(() => {
    if (expanded && inView) {
      setDynamicHeight(old => (height > 394 ? height : old));
    }
  }, [expanded, height, inView]);

  const emptyDiv = (
    <div style={{ height: dynamicHeight }}>
      <span className={'show-for-sr'}>prerendered</span>
    </div>
  );

  return (
    <PostEditorGroupDataProvider postGroupIndex={index}>
      <div
        id={`postGroupContainer-${index}`}
        data-index={index}
        data-expanded={expanded}
        className={classNames(
          classes.PostEditorBulkImportWrapper,
          !totalErrors && !!totalWarrnings && !expanded ? classes.PostEditorBulkImportWrapperWarning : undefined,
          !!totalErrors && !expanded ? classes.PostEditorBulkImportWrapperError : undefined,
          !!totalErrors && (!expanded || (expanded && !inView)) ? POST_EDITOR_ERROR_CLASS : undefined
        )}
        onClick={() => {
          !expanded && dispatch({ type: 'expandPostGroup', payload: { index, ref: wrapperRef } });
        }}
        ref={wrapperRef}>
        <Debugger tooltipContent={'Post Group: ' + index} modalContent={postGroup} />
        <div className={classNames(expanded ? classes.ExpandWrapperExpanded : classes.ExpandWrapperCollapsed)}>
          <div ref={ref}>
            {inView ? (
              <div style={{ ...(animated ? { height: dynamicHeight } : {}) }}>
                <div ref={elementRef} className={expanded ? undefined : classes.InViewWrapper}>
                  <div className={classes.DeletePostGroup}>
                    <Button
                      action={e => {
                        // we needed to stop propagation, because it could have propagated to the wrapper onClick, that
                        // would have set expanded to true and that would result in initialisating the editor
                        e.stopPropagation();
                        removeGroupAction(index);
                      }}
                      type={'a'}
                      variant={'hollow'}
                      size={'small'}
                      color={ButtonColorsEnum.primary}
                      icon={{ side: 'center', value: IconsEnum.trash }}
                    />
                  </div>
                  <PostEditorHeader formInstance={formInstance} />
                  <PostEditorBody
                    formInstance={formInstance}
                    isBulkImport={true}
                    expanded={expanded}
                    counter={counter}
                  />
                </div>
              </div>
            ) : (
              emptyDiv
            )}
          </div>
          {!expanded && inView ? (
            <div className={classes.ExpanderWrapper}>
              <div className={classNames('grid-x', 'align-center', 'align-bottom', classes.ExpanderGridWrapper)}>
                <div className={'cell shrink'}>
                  <div className={classes.ShowMoreWidth}>
                    <span>Show more</span>
                    <Icon name={IconsEnum.triangleDown} size={'medium'}></Icon>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </PostEditorGroupDataProvider>
  );
};
